// src/components/CoverImageEditor.js
// Reusable program cover editor (Programs library detail panel + Program Builder).
// Landscape cover: upload an image, then zoom + drag to frame it. Stores the full
// image URL + framing (zoom/posX/posY) so it's re-adjustable and rendered via CSS —
// no server-side crop. Reuses the equipment-style getUploadUrl upload (program-covers/).
//
// Props:
//   program : the program object (reads coverImageUrl / coverZoom / coverPosX / coverPosY)
//   onClose : () => void
//   onSave  : (programId, { coverImageUrl, coverZoom, coverPosX, coverPosY }) => Promise

import React, { useState, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

function CoverImageEditor({ program, onClose, onSave }) {
  const [file, setFile] = useState(null);
  const [localUrl, setLocalUrl] = useState(program.coverImageUrl || null); // preview src
  const [zoom, setZoom] = useState(program.coverZoom || 1.4);
  const [pos, setPos] = useState({ x: program.coverPosX ?? 50, y: program.coverPosY ?? 50 }); // % object-position
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const dragRef = useRef(null);      // { startX, startY, startPosX, startPosY }
  const frameRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    setError(null);
    setFile(f);
    setLocalUrl(URL.createObjectURL(f));
    setZoom(1.4); setPos({ x: 50, y: 50 });   // reset framing for a new image
  };

  // drag to reposition (translate object-position % inversely to mouse drag)
  const onDown = (e) => {
    if (!localUrl) return;
    const pt = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: pt.clientX, startY: pt.clientY, startPosX: pos.x, startPosY: pos.y };
  };
  const onMove = (e) => {
    if (!dragRef.current || !frameRef.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const rect = frameRef.current.getBoundingClientRect();
    // drag right -> show more of the LEFT of the image -> object-position x decreases
    const dx = (pt.clientX - dragRef.current.startX) / rect.width * 100;
    const dy = (pt.clientY - dragRef.current.startY) / rect.height * 100;
    const nx = Math.max(0, Math.min(100, dragRef.current.startPosX - dx));
    const ny = Math.max(0, Math.min(100, dragRef.current.startPosY - dy));
    setPos({ x: nx, y: ny });
  };
  const onUp = () => { dragRef.current = null; };

  const save = async () => {
    setError(null); setUploading(true);
    try {
      let coverImageUrl = program.coverImageUrl || null;
      if (file) {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `program-covers/${timestamp}_${safeName}`;
        const getUploadUrl = httpsCallable(getFunctions(), 'getUploadUrl');
        const result = await getUploadUrl({ fileName, contentType: file.type });
        await fetch(result.data.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        coverImageUrl = result.data.fileUrl;
      }
      if (!coverImageUrl) { setError('Choose an image first.'); setUploading(false); return; }
      await onSave(program.id, { coverImageUrl, coverZoom: zoom, coverPosX: pos.x, coverPosY: pos.y });
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save cover.');
    } finally { setUploading(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => !uploading && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}
        onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchMove={onMove} onTouchEnd={onUp}>
        <div className="modal-header">
          <div className="modal-title">Program Cover</div>
          <button className="modal-close" onClick={() => !uploading && onClose()}>×</button>
        </div>

        {/* landscape frame preview (16:9) */}
        <div
          ref={frameRef}
          style={S.coverFrame}
          onMouseDown={onDown} onTouchStart={onDown}
        >
          {localUrl ? (
            <img
              src={localUrl}
              alt="cover"
              draggable={false}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                objectPosition: `${pos.x}% ${pos.y}%`,
                transform: `scale(${zoom})`, transformOrigin: `${pos.x}% ${pos.y}%`,
                cursor: 'grab', userSelect: 'none',
              }}
            />
          ) : (
            <div style={S.coverEmpty}>No image yet — choose one below.</div>
          )}
        </div>

        {/* zoom slider (only meaningful with an image) */}
        {localUrl && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#9aa0a8', fontWeight: 600 }}>ZOOM</span>
            <input type="range" min="1" max="3" step="0.01" value={zoom}
              onChange={e => setZoom(parseFloat(e.target.value))} style={{ flex: 1 }} />
          </div>
        )}
        <div style={{ fontSize: 12, color: '#9aa0a8', marginTop: 6 }}>
          {localUrl ? 'Drag the image to reposition. Zoom to fill the frame.' : ''}
        </div>

        {/* file input */}
        <div style={{ marginTop: 12 }}>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {localUrl ? 'Choose a different image' : 'Choose image'}
            <input type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />
          </label>
        </div>

        {error && <div style={{ color: '#E63946', fontSize: 13, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={uploading || !localUrl}>
            {uploading ? 'Saving…' : 'Save Cover'}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  coverFrame: { position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#1a1d21', overflow: 'hidden', borderRadius: 0, cursor: 'grab' },
  coverEmpty: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 13 },
};

export default CoverImageEditor;